import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';

import { appNamedRoutes, isPermitted } from '../../routes';
import { hasNewCommunicationDashboardFeature } from '../../utils/features';
import { useUnreadCount, useMarkAllRead } from '../../hooks/useUnreadCount';
import { useConversations } from '../../hooks/useConversations';

const PREVIEW_PER_PAGE = 10;

//
// Unread messages bell with a preview dropdown.
//
// The list reuses the existing paginated unread endpoint through useConversations
// ({ unreadOnly: true }) rather than a bespoke one — it already returns name, avatar,
// last message and timestamp per conversation. The query only runs while the panel is
// open, so the closed bell costs nothing beyond the count poll.
//
const UnreadMessagesBell = ({ auth, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const observerRef = useRef(null);
  const history = useHistory();
  const location = useLocation();

  const visible = isPermitted('accountPlayers', auth) && hasNewCommunicationDashboardFeature(auth);
  const unreadCount = useUnreadCount({ enabled: visible });
  const { mutate: markAllRead, isPending: isMarkingAllRead } = useMarkAllRead();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useConversations({
    unreadOnly: true,
    per: PREVIEW_PER_PAGE,
    enabled: visible && open,
  });

  // The navbar survives route changes, so the panel has to be closed explicitly on
  // navigation — otherwise picking a conversation leaves it hanging over the page.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return undefined;

    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };

    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // Infinite scroll: a sentinel at the end of the list pulls the next page as it comes
  // into view. Same approach as ConversationList — watching the sentinel rather than
  // scroll offsets keeps pagination working regardless of how many rows fit the panel.
  const loaderRef = useCallback((node) => {
    if (isLoading || isFetchingNextPage) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
    });

    if (node) observerRef.current.observe(node);
  }, [isLoading, isFetchingNextPage, fetchNextPage, hasNextPage]);

  // The panel unmounts on close, so the observer has to be torn down with it.
  useEffect(() => () => {
    if (observerRef.current) observerRef.current.disconnect();
  }, []);

  const conversations = (data?.pages || []).flatMap(page => page.data || []);

  const openConversation = (playerId) => {
    setOpen(false);
    history.push({
      pathname: appNamedRoutes.communicationIndividuals,
      search: `?filter=unread&id=${playerId}`,
    });
  };

  const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
  };

  if (!visible) return null;

  const hasUnread = unreadCount > 0;

  const handleBellClick = () => setOpen(o => !o);

  const label = hasUnread ? t('nav.unreadMessages') : t('nav.messaging');

  return (
    <div className="o-navbar__bell-wrap" ref={ref}>
      <button
        type="button"
        className="o-navbar__bell"
        onClick={handleBellClick}
        title={label}
        aria-label={label}
        aria-expanded={open}
      >
        <svg className="o-navbar__bell-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
        </svg>
        {hasUnread && (
          <span className="o-navbar__bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="o-navbar__bell-panel">
          <div className="o-navbar__bell-panel-header">
            <span>{label}</span>
            {hasUnread && (
              <button
                type="button"
                className="o-navbar__bell-mark-all"
                onClick={() => markAllRead()}
                disabled={isMarkingAllRead}
              >
                {t('nav.markAllRead')}
              </button>
            )}
          </div>

          <div className="o-navbar__bell-list">
            {isLoading && (
              <div className="o-navbar__bell-empty">{t('communication.loading')}</div>
            )}

            {!isLoading && conversations.length === 0 && (
              <div className="o-navbar__bell-empty">{t('communication.noUnreadMessages')}</div>
            )}

            {conversations.map(conversation => (
              <button
                type="button"
                key={conversation.id}
                className="o-navbar__bell-item"
                onClick={() => openConversation(conversation.id)}
              >
                <div className="o-navbar__bell-item-top">
                  <span className="o-navbar__bell-item-name">{conversation.name}</span>
                  <span className="o-navbar__bell-item-date">
                    {formatDate(conversation.lastMessageAt)}
                  </span>
                </div>
                <div className="o-navbar__bell-item-message">{conversation.lastMessage}</div>
              </button>
            ))}

            {hasNextPage && <div ref={loaderRef} />}

            {isFetchingNextPage && (
              <div className="o-navbar__bell-empty">{t('communication.loading')}</div>
            )}
          </div>

          <button
            type="button"
            className="o-navbar__bell-footer"
            onClick={() => {
              setOpen(false);
              history.push({
                pathname: appNamedRoutes.communicationIndividuals,
                search: '?filter=unread',
              });
            }}
          >
            {t('nav.viewAllUnread')}
          </button>
        </div>
      )}
    </div>
  );
};

UnreadMessagesBell.propTypes = {
  auth: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

export default UnreadMessagesBell;
